import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { fetchPatient } from "../../store/slices/patientsSlice";
import { DRP_SECTIONS, DRP_LIKERT_LEGEND } from "./drpClientFeedbackConfig";

const DrpClientFeedbackForm = ({ formData, setFormData, readOnly = false }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (formData.patient_id && !formData.client_name) {
      dispatch(fetchPatient(formData.patient_id))
        .unwrap()
        .then((p) => {
          setFormData((prev) => ({
            ...prev,
            client_name: `${p.first_name || ""} ${p.last_name || ""}`.trim(),
            mr_no: p.mr_no || prev.mr_no,
          }));
        })
        .catch((err) => console.error("Failed to prefill patient:", err));
    }
  }, [dispatch, formData.patient_id, formData.client_name, setFormData]);

  const handleChange = (e) => {
    if (readOnly) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const setRating = (key, val) => {
    if (readOnly) return;
    setFormData((prev) => ({ ...prev, [key]: val }));
  };

  return (
    <div className="p-3" style={{ maxHeight: "70vh", overflowY: "auto", scrollbarWidth: "thin" }}>
      {/* Header + Likert legend */}
      <div className="text-center mb-3">
        <h5 className="fw-bold mb-0">THE RECOVERY HOUSE (TRH)</h5>
        <div className="fw-semibold">DAY REHAB PROGRAMME (DRP)</div>
        <div className="text-muted small">Client Feedback &amp; Discharge Interview Form</div>
      </div>
      <div className="alert alert-light border mb-3">
        <div className="fw-bold mb-1">Likert Scale for all items</div>
        <div className="row g-1 small">
          {DRP_LIKERT_LEGEND.map((l) => (
            <div className="col-md-12" key={l.n}><strong>{l.n} = {l.label}</strong></div>
          ))}
        </div>
      </div>

      <div className="accordion" id="drpFeedbackAccordion">
        {/* Basic info */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold" type="button" data-bs-toggle="collapse" data-bs-target="#drpBasic">
              Client Information
            </button>
          </h2>
          <div id="drpBasic" className="accordion-collapse collapse show">
            <div className="accordion-body pt-3">
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="client_name" value={formData.client_name || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Client Name</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="mr_no" value={formData.mr_no || ""} onChange={handleChange} disabled={readOnly} />
                    <label>MR No</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-floating form-floating-outline">
                    <input type="date" className="form-control" name="date_of_admission" value={formData.date_of_admission || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Date of Admission</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-floating form-floating-outline">
                    <input type="date" className="form-control" name="date_of_discharge" value={formData.date_of_discharge || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Date of Discharge</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Likert sections */}
        {DRP_SECTIONS.map((section, sIdx) => (
          <div className="accordion-item mb-3 shadow-sm rounded" key={section.title}>
            <h2 className="accordion-header">
              <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target={`#drpSec${sIdx}`}>
                {section.title}
              </button>
            </h2>
            <div id={`drpSec${sIdx}`} className="accordion-collapse collapse">
              <div className="accordion-body pt-3">
                <div className="table-responsive">
                  <table className="table table-bordered align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Question</th>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <th className="text-center" style={{ width: "8%" }} key={n}>{n}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {section.questions.map((q) => (
                        <React.Fragment key={q.key}>
                          {q.subheading && (
                            <tr className="table-secondary">
                              <td colSpan={6} className="fw-semibold small">{q.subheading}</td>
                            </tr>
                          )}
                          <tr>
                            <td className="small">{q.label}</td>
                            {[1, 2, 3, 4, 5].map((n) => (
                              <td className="text-center" key={n}>
                                <input type="radio" className="form-check-input" name={q.key} checked={Number(formData[q.key]) === n} onChange={() => setRating(q.key, n)} disabled={readOnly} />
                              </td>
                            ))}
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Section 14: Additional Feedback */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#drpAdditional">
              Section 14: Additional Feedback
            </button>
          </h2>
          <div id="drpAdditional" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <label className="form-label">Please provide suggestions for improvement:</label>
              <textarea className="form-control" rows="5" name="additional_feedback" value={formData.additional_feedback || ""} onChange={handleChange} disabled={readOnly}></textarea>
            </div>
          </div>
        </div>

        {/* For Office Use Only */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#drpOffice">
              For Office Use Only
            </button>
          </h2>
          <div id="drpOffice" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="reviewed_by" value={formData.reviewed_by || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Reviewed by</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="reviewer_signature" value={formData.reviewer_signature || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Signature</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="date" className="form-control" name="review_date" value={formData.review_date || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Date</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DrpClientFeedbackForm;
